import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronRight } from 'react-icons/fa';
import { PORTRAIT_GALLERY_ITEMS } from '../data/portraitGalleryItems';
import { contentPlannerService } from '../utils/apiService';
import { API_ENDPOINTS } from '../config/api';

const Section = styled.section`
  padding: 56px 0 72px;
  background: linear-gradient(180deg, #f0f2f4 0%, var(--light-gray) 40%, #eceff2 100%);
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  color: var(--secondary-color);
  margin-bottom: 10px;
  letter-spacing: -0.02em;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  color: var(--secondary-color);
  opacity: 0.85;
  margin-bottom: 40px;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
`;

const CarouselRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(8px, 2.5vw, 24px);
  min-height: min(74vh, 640px);
  perspective: 1200px;
  padding: 8px 0 28px;

  @media (max-width: 768px) {
    min-height: min(64vh, 540px);
    gap: 6px;
  }
`;

const CardShell = styled(motion.div)`
  flex-shrink: 0;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  border: 3px solid #fff;
  cursor: ${props => (props.$side ? 'pointer' : 'default')};
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 9 / 16;
  width: ${props => props.$width}px;
  max-height: min(74vh, 640px);

  @media (max-width: 768px) {
    width: ${props => props.$widthMobile}px;
    max-height: min(64vh, 540px);
    border-radius: 16px;
    border-width: 2px;
  }
`;

const MediaBox = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #e8eaed, #f5f6f8);
`;

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
  background: #000;
`;

const NavFab = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  z-index: 3;
  transition: var(--transition);

  &:hover {
    background: var(--primary-color);
    color: #fff;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    right: 6px;
  }
`;

const PillBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
`;

const DotStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: #fff;
  border-radius: 999px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
`;

const StripDot = styled.button`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${props => (props.$active ? props.$color : 'transparent')};
  background: ${props => props.$color};
  cursor: pointer;
  padding: 0;
  opacity: ${props => (props.$active ? 1 : 0.55)};
  transform: ${props => (props.$active ? 'scale(1.15)' : 'scale(1)')};
  transition: transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${props =>
    props.$active ? `0 0 0 3px rgba(255, 140, 0, 0.25)` : 'none'};

  &:hover {
    opacity: 1;
  }
`;

const CaptionText = styled(motion.p)`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-align: center;
`;

const DOT_COLORS = ['#e85d4c', '#f4b400', '#34a853', '#4285f4'];

function GalleryCard({ item, variant, reduceMotion, onNext, videoRef, onVideoEnded }) {
  const isCenter = variant === 'center';
  const showVideo = item.type === 'video' && isCenter && !reduceMotion;

  return (
    <MediaBox>
      {item.type === 'image' && (
        <StyledImg
          src={item.src}
          alt={item.caption || 'Galeri görseli'}
          loading="lazy"
          decoding="async"
        />
      )}
      {item.type === 'video' && !showVideo && (
        <StyledImg
          src={item.poster}
          alt={item.caption || 'Video önizleme'}
          loading="lazy"
          decoding="async"
        />
      )}
      {item.type === 'video' && showVideo && (
        <StyledVideo
          ref={videoRef}
          poster={item.poster}
          playsInline
          controls
          /* loop kapalı: bitince slayt ilerler. Ses: tarayıcı izin vermezse play() içinde sessize düşer */
          onEnded={onVideoEnded}
        >
          {item.webm && <source src={item.webm} type="video/webm" />}
          <source src={item.mp4} type="video/mp4" />
        </StyledVideo>
      )}
      {isCenter && (
        <NavFab type="button" onClick={onNext} aria-label="Sonraki">
          <FaChevronRight size={18} />
        </NavFab>
      )}
    </MediaBox>
  );
}

const PortraitGallerySection = () => {
  const [galleryItems, setGalleryItems] = useState(PORTRAIT_GALLERY_ITEMS);
  const len = galleryItems.length;
  const [active, setActive] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const videoRef = useRef(null);

  const prev = (active - 1 + len) % len;
  const next = (active + 1) % len;

  useEffect(() => {
    const fetchPlannerItems = async () => {
      try {
        const data = await contentPlannerService.getItems({
          activeOnly: true,
          orientation: 'vertical'
        });
        if (!Array.isArray(data) || data.length === 0) return;
        const mapped = data
          .map((item) => {
            if (item.media_type === 'image') {
              return {
                type: 'image',
                src: API_ENDPOINTS.UPLOADS(item.file_url),
                caption: item.caption || item.title
              };
            }
            if (item.media_type === 'video') {
              return {
                type: 'video',
                mp4: API_ENDPOINTS.UPLOADS(item.file_url),
                webm: '',
                poster: item.poster_url ? API_ENDPOINTS.UPLOADS(item.poster_url) : '/images/hero/hero-video-poster.jpg',
                caption: item.caption || item.title
              };
            }
            return null;
          })
          .filter(Boolean);
        if (mapped.length > 0) setGalleryItems(mapped);
      } catch (error) {
        console.error('İçerik planlayıcı verisi alınamadı, varsayılan galeri kullanılıyor:', error.message);
      }
    };

    fetchPlannerItems();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const go = useCallback(
    (dir) => {
      setActive((i) => (i + dir + len) % len);
      setAutoPlay(false);
      setTimeout(() => setAutoPlay(true), 12000);
    },
    [len]
  );

  const advanceAfterVideo = useCallback(() => {
    setActive((i) => (galleryItems[i]?.type === 'video' ? (i + 1) % len : i));
  }, [galleryItems, len]);

  /* Görsel slaytlar: sabit süre. Video slayt (sesli oynatma): bitene kadar bekle — onEnded */
  useEffect(() => {
    if (len === 0) return undefined;
    if (!autoPlay) return undefined;

    if (reduceMotion) {
      const t = setInterval(() => setActive((i) => (i + 1) % len), 4500);
      return () => clearInterval(t);
    }

    if (galleryItems[active]?.type === 'video') return undefined;

    const t = setInterval(() => setActive((i) => (i + 1) % len), 4500);
    return () => clearInterval(t);
  }, [autoPlay, reduceMotion, len, active, galleryItems]);

  useEffect(() => {
    if (len === 0) return undefined;
    const item = galleryItems[active];
    if (item.type !== 'video' || reduceMotion || !videoRef.current) return undefined;
    const v = videoRef.current;
    v.pause();
    try {
      v.currentTime = 0;
    } catch (e) {
      /* ignore */
    }
    v.muted = false;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        v.muted = true;
        v.play().catch(() => {});
      });
    }
    return () => {
      v.pause();
    };
  }, [active, reduceMotion, galleryItems]);

  const sideAnim = (side) => ({
    scale: reduceMotion ? 0.88 : side === 'left' ? 0.8 : 0.8,
    filter: reduceMotion ? 'none' : 'blur(7px)',
    opacity: reduceMotion ? 0.9 : 0.72,
    rotateY: reduceMotion ? 0 : side === 'left' ? 8 : -8
  });

  const centerW = 380;
  const centerWMobile = 280;
  const sideW = 205;
  const sideWMobile = 150;

  const transition = reduceMotion
    ? { duration: 0 }
    : { type: 'spring', stiffness: 260, damping: 28 };

  if (len === 0) return null;

  return (
    <Section aria-label="Portre galeri">
      <Inner>
        <SectionTitle>Sultanlar’dan kareler</SectionTitle>
        <SectionSubtitle>
          Afiş ve tanıtım görsellerimizi tam boyutta inceleyin; ortadaki karta tıklayarak veya ok ile
          gezinin.
        </SectionSubtitle>

        <CarouselRow>
          <CardShell
            $width={sideW}
            $widthMobile={sideWMobile}
            $side
            onClick={() => go(-1)}
            initial={false}
            animate={sideAnim('left')}
            transition={transition}
            style={{ zIndex: 1 }}
          >
            <GalleryCard item={galleryItems[prev]} variant="side" reduceMotion={reduceMotion} />
          </CardShell>

          <CardShell
            $width={centerW}
            $widthMobile={centerWMobile}
            $side={false}
            initial={false}
            animate={{
              scale: 1,
              filter: 'none',
              opacity: 1,
              rotateY: 0
            }}
            transition={transition}
            style={{ zIndex: 2 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: reduceMotion ? 1 : 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: reduceMotion ? 1 : 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.35 }}
                style={{ width: '100%', height: '100%' }}
              >
                <GalleryCard
                  item={galleryItems[active]}
                  variant="center"
                  reduceMotion={reduceMotion}
                  onNext={() => go(1)}
                  videoRef={videoRef}
                  onVideoEnded={advanceAfterVideo}
                />
              </motion.div>
            </AnimatePresence>
          </CardShell>

          <CardShell
            $width={sideW}
            $widthMobile={sideWMobile}
            $side
            onClick={() => go(1)}
            initial={false}
            animate={sideAnim('right')}
            transition={transition}
            style={{ zIndex: 1 }}
          >
            <GalleryCard item={galleryItems[next]} variant="side" reduceMotion={reduceMotion} />
          </CardShell>
        </CarouselRow>

        <PillBar>
          <DotStrip>
            {galleryItems.map((_, i) => (
              <StripDot
                key={i}
                type="button"
                $color={DOT_COLORS[i % DOT_COLORS.length]}
                $active={i === active}
                onClick={() => {
                  setActive(i);
                  setAutoPlay(false);
                  setTimeout(() => setAutoPlay(true), 12000);
                }}
                aria-label={`Öğe ${i + 1}`}
              />
            ))}
          </DotStrip>
          <AnimatePresence mode="wait">
            <CaptionText
              key={active}
              initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : -6 }}
              transition={{ duration: reduceMotion ? 0 : 0.25 }}
            >
              {galleryItems[active].caption}
            </CaptionText>
          </AnimatePresence>
        </PillBar>
      </Inner>
    </Section>
  );
};

export default PortraitGallerySection;
